import type {
  InventoryItemRecord,
  InventorySupplierInvoiceRecord,
  InventorySupplierInvoiceStatus,
  SaveSupplierInvoiceRequest,
} from '@/renderer/types/inventory';

export interface SupplierInvoiceLineFormState {
  tempId: string;
  id?: string;
  inventoryItemId: string;
  description: string;
  quantityPurchased: string;
  purchaseUnitId: string;
  conversionRate: string;
  quantityInStockUnit: string;
  unitCost: string;
  taxRate: string;
  taxAmount: string;
  lineTotal: string;
}

export interface SupplierInvoiceFormState {
  supplierId: string;
  stockLocationId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  notes: string;
  lines: SupplierInvoiceLineFormState[];
}

let lineCounter = 0;
const nextTempId = () => {
  lineCounter += 1;
  return `line-${lineCounter}`;
};

export function createEmptyInvoiceLine(): SupplierInvoiceLineFormState {
  return {
    tempId: nextTempId(),
    inventoryItemId: '',
    description: '',
    quantityPurchased: '',
    purchaseUnitId: '',
    conversionRate: '1',
    quantityInStockUnit: '',
    unitCost: '',
    taxRate: '0',
    taxAmount: '0',
    lineTotal: '0',
  };
}

export function createEmptySupplierInvoiceForm(): SupplierInvoiceFormState {
  const today = new Date().toISOString().split('T')[0];
  return {
    supplierId: '',
    stockLocationId: '',
    invoiceNumber: '',
    invoiceDate: today,
    dueDate: '',
    notes: '',
    lines: [createEmptyInvoiceLine()],
  };
}

export function createSupplierInvoiceFormFromRecord(
  record: InventorySupplierInvoiceRecord,
): SupplierInvoiceFormState {
  return {
    supplierId: record.supplier_id,
    stockLocationId: record.stock_location_id,
    invoiceNumber: record.invoice_number ?? '',
    invoiceDate: record.invoice_date,
    dueDate: record.due_date ?? '',
    notes: record.notes ?? '',
    lines: (record.lines ?? []).map(
      (line): SupplierInvoiceLineFormState => ({
        tempId: nextTempId(),
        id: line.id,
        inventoryItemId: line.inventory_item_id,
        description: line.description ?? '',
        quantityPurchased: String(line.quantity_purchased),
        purchaseUnitId: line.purchase_unit_id ?? '',
        conversionRate: String(line.conversion_rate),
        quantityInStockUnit: String(line.quantity_in_stock_unit),
        unitCost: String(line.unit_cost),
        taxRate: String(line.tax_rate),
        taxAmount: String(line.tax_amount),
        lineTotal: String(line.line_total),
      }),
    ),
  };
}

export function recalculateLine(
  line: SupplierInvoiceLineFormState,
): SupplierInvoiceLineFormState {
  const qty = parseFloat(line.quantityPurchased) || 0;
  const cost = parseFloat(line.unitCost) || 0;
  const taxRate = parseFloat(line.taxRate) || 0;
  const conversion = parseFloat(line.conversionRate) || 1;

  const preTax = qty * cost;
  const taxAmount = parseFloat(((preTax * taxRate) / 100).toFixed(2));
  const lineTotal = parseFloat((preTax + taxAmount).toFixed(2));
  const quantityInStockUnit = parseFloat((qty * conversion).toFixed(4));

  return {
    ...line,
    quantityInStockUnit: String(quantityInStockUnit),
    taxAmount: String(taxAmount),
    lineTotal: String(lineTotal),
  };
}

export function applyItemToLine(
  line: SupplierInvoiceLineFormState,
  item: InventoryItemRecord,
): SupplierInvoiceLineFormState {
  return recalculateLine({
    ...line,
    inventoryItemId: item.id,
    purchaseUnitId: item.purchase_unit_id,
    conversionRate: String(item.conversion_rate),
    quantityInStockUnit: '',
  });
}

export function computeInvoiceTotals(lines: SupplierInvoiceLineFormState[]) {
  const { subtotal, taxAmount } = lines.reduce(
    (acc, line) => {
      const qty = parseFloat(line.quantityPurchased) || 0;
      const cost = parseFloat(line.unitCost) || 0;
      const lineTax = parseFloat(line.taxAmount) || 0;
      return {
        subtotal: acc.subtotal + qty * cost,
        taxAmount: acc.taxAmount + lineTax,
      };
    },
    { subtotal: 0, taxAmount: 0 },
  );

  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    taxAmount: parseFloat(taxAmount.toFixed(2)),
    totalAmount: parseFloat((subtotal + taxAmount).toFixed(2)),
  };
}

export function buildSaveSupplierInvoicePayload(
  companyId: number,
  outletId: string,
  form: SupplierInvoiceFormState,
  editId?: string,
): SaveSupplierInvoiceRequest {
  const { subtotal, taxAmount, totalAmount } = computeInvoiceTotals(form.lines);

  return {
    id: editId,
    companyId,
    outletId,
    supplierId: form.supplierId,
    stockLocationId: form.stockLocationId,
    invoiceNumber: form.invoiceNumber.trim() || null,
    invoiceDate: form.invoiceDate,
    dueDate: form.dueDate || null,
    subtotal,
    taxAmount,
    totalAmount,
    notes: form.notes.trim() || null,
    lines: form.lines
      .filter((line) => line.inventoryItemId)
      .map((line) => ({
        id: line.id,
        inventoryItemId: line.inventoryItemId,
        description: line.description.trim() || null,
        quantityPurchased: parseFloat(line.quantityPurchased) || 0,
        purchaseUnitId: line.purchaseUnitId || null,
        conversionRate: parseFloat(line.conversionRate) || 1,
        quantityInStockUnit: parseFloat(line.quantityInStockUnit) || 0,
        unitCost: parseFloat(line.unitCost) || 0,
        taxRate: parseFloat(line.taxRate) || 0,
        taxAmount: parseFloat(line.taxAmount) || 0,
        lineTotal: parseFloat(line.lineTotal) || 0,
      })),
  };
}

export const INVOICE_STATUS_LABELS: Record<
  InventorySupplierInvoiceStatus,
  string
> = {
  draft: 'Draft',
  posted: 'Posted',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  voided: 'Voided',
};

export const INVOICE_STATUS_BADGE_CLASS: Record<
  InventorySupplierInvoiceStatus,
  string
> = {
  draft: 'bg-yellow-50 text-yellow-700',
  posted: 'bg-blue-50 text-blue-700',
  partially_paid: 'bg-orange-50 text-orange-700',
  paid: 'bg-emerald-50 text-emerald-700',
  voided: 'bg-gray-100 text-gray-600',
};
